import {
  LanguageNameSpaces,
  LineChartDataPointWithExchange,
  PrecisionType,
  TradeData,
  TradeStatus,
} from "@shared/types";
import Flex from "common/Boxes/Flex";
import Title from "common/Typography/Title";
import React, { useCallback, useMemo } from "react";
import styled, { useTheme } from "styled-components";
import { useSelector } from "react-redux";
import { selectDisplayCurrency } from "@shared/slices/common.slice";
import useHistoryWithQuery from "utils/hooks/useHistoryWithQuery";
import InactiveAccountIcon from "features/configuration/common/InactiveAccountIcon";
import { AccountObjectType } from "@shared/features/settings/types";
import { useInvalidAccount } from "@shared/features/settings/hooks";
import AntClockCircleOutlined from "@ant-design/icons/ClockCircleOutlined";
import { ICON_TYPES, TRADE_EXTRA_DETAILS_TABS } from "@shared/common/constants";
import { getLiveTradeTimeDuration } from "@shared/utils/date";
import { getTradeExtraDetailsRoute } from "utils/routes";
import { DISPLAY_CURRENCIES } from "common/constants";
import { RawLineChartDataPoint } from "types";
import PnlComponent from "features/dashboard/OpenTrades/PnlComponent";
import { useBottomNavigator } from "utils/hooks/misc";
import TextWithPrecisionAndIcon from "common/Text/TextWithPrecisionAndIcon";
import { usePrecisions } from "@shared/utils/hooks/usePrecisions";
import { ClosedTradeStatus } from "@shared/features/trades/common/constants";
import usePricesSocket from "@shared/services/socket/usePricesSocket";
import { getBotActiveTradeClosestOrdersAnnotations } from "@shared/features/trades/common/utils";
import { FireFilled } from "features/dashboard/common/styles";
import { getUsdValueWithPrecision } from "features/dashboard/common/utils";
import IconWithSource from "features/trades/common/IconWithSource.tsx";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { useDisplayTradeDetails } from "@shared/utils/hooks/misc.ts";

interface UseTradeCardProps {
  tradeData: TradeData;
  graphData?: LineChartDataPointWithExchange[] | RawLineChartDataPoint[];
}

const useTradeCard = ({ tradeData, graphData }: UseTradeCardProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.DASHBOARD);
  const theme = useTheme();
  const displayCurrency = useSelector(selectDisplayCurrency);
  const { baseShouldDisplay: isBottomNavigator } = useBottomNavigator();
  const { historyPush } = useHistoryWithQuery();
  const { getPrecision } = usePrecisions();
  const balancePrecision = getPrecision(PrecisionType.BALANCE);
  const pricePrecision = getPrecision(
    PrecisionType.PRICE,
    tradeData.symbol,
    tradeData.exchange
  );
  const isHot = tradeData.closest_order?.is_hot;
  const { getInvalidAccountData } = useInvalidAccount();
  const invalidAccountData = useMemo(
    () => getInvalidAccountData(tradeData.client_id),
    [tradeData, getInvalidAccountData]
  );
  const liveTradeTimeDuration = getLiveTradeTimeDuration(
    tradeData.timestamp,
    graphData
  );
  const tradeTimeDuration =
    liveTradeTimeDuration || tradeData.graph_data?.time_passed;
  const isTradeClosed =
    tradeData.status_value &&
    tradeData.status_value !== TradeStatus.EDITED &&
    ClosedTradeStatus.has(tradeData.status_value);
  const displayTradeDetails = useDisplayTradeDetails();

  const { socketPrice } = usePricesSocket(tradeData.symbol, tradeData.exchange);

  const annotations = useMemo(() => {
    return getBotActiveTradeClosestOrdersAnnotations(
      tradeData,
      theme,
      undefined,
      socketPrice.value,
      pricePrecision
    );
  }, [tradeData, theme, pricePrecision, socketPrice.value]);

  const _openTradeExtraDetails = useCallback(() => {
    historyPush(
      getTradeExtraDetailsRoute(
        location.pathname,
        tradeData.id,
        TRADE_EXTRA_DETAILS_TABS.TIMELINE
      )
    );
  }, [tradeData]);

  const openTradeExtraDetails = displayTradeDetails
    ? _openTradeExtraDetails
    : undefined;

  const getSymbol = () => {
    const symbol = (
      <Flex $justifyContent={"start"}>
        <TradeSymbolTitle>{tradeData.symbol}</TradeSymbolTitle>
        {isHot && !invalidAccountData.isInvalidOrInactive && <FireFilled />}
      </Flex>
    );

    return (
      <InactiveAccountIcon
        accountId={tradeData.client_id}
        objectType={AccountObjectType.TRADE}
      >
        {symbol}
      </InactiveAccountIcon>
    );
  };

  const topSection = useMemo(
    () => (
      <Flex $alignItems="start">
        <Flex>
          <IconWithSource
            symbol={tradeData.symbol}
            sourceType={tradeData.source_type}
            source={tradeData.group}
            mainIconSize={30}
            style={{ marginRight: 8 }}
          />
          <SymbolLeverageWrapper>
            {getSymbol()}
            <MarginWrapper>
              <Title level={5} type="secondary">
                {tradeData["Long/Short"]}
              </Title>
              {tradeData.margin_type && (
                <>
                  <div
                    style={{
                      height: 12,
                      margin: "0 6px 0 7px",
                      borderLeft: theme.borders.hard,
                    }}
                  />
                  <MarginTitle>
                    {tradeData.margin_type}{" "}
                    {tradeData.leverage ? `${tradeData.leverage}x` : ""}
                  </MarginTitle>
                </>
              )}
            </MarginWrapper>
          </SymbolLeverageWrapper>
        </Flex>
        <Title level={4} type="secondary">
          <ClockCircleOutlined />
          {tradeTimeDuration}
        </Title>
      </Flex>
    ),
    [tradeData, tradeTimeDuration, theme, invalidAccountData]
  );

  const isBtc = displayCurrency === DISPLAY_CURRENCIES.BTC;
  const total = useMemo(
    () =>
      isBtc
        ? tradeData.allocated_amount_btc
        : getUsdValueWithPrecision(tradeData.allocated_amount_usd),
    [isBtc, tradeData]
  );
  const totalByCurrency = (
    <TextWithPrecisionAndIcon
      text={total}
      precision={balancePrecision}
      iconType={ICON_TYPES.DISPLAY_CURRENCY}
      iconStyle={isBtc ? { fontSize: 12, marginBottom: 2 } : undefined}
    />
  );

  const fontSize = useMemo(() => {
    const strValueLength = total?.toString().replace(/\./g, "").length;
    if (strValueLength >= 10) return isBottomNavigator ? 11 : 12;
    if (strValueLength >= 6) return isBottomNavigator ? 12 : 14;
    else return isBottomNavigator ? 13 : 16;
  }, [total]);

  const bottomSection = useMemo(
    () => (
      <InfoWrapper>
        <InfoItem style={{ width: "40%" }}>
          <InfoTitle>{t("open_trades.amount_label")}</InfoTitle>
          <Title as="div" style={{ fontSize }}>
            {totalByCurrency}
          </Title>
        </InfoItem>
        <VerticalBorder />
        <InfoItem style={{ width: "60%" }}>
          <InfoTitle>{t("open_trades.pnl_label")}</InfoTitle>
          <PnlComponent tradeData={tradeData} fontSize={fontSize} />
        </InfoItem>
      </InfoWrapper>
    ),
    [fontSize, tradeData, totalByCurrency]
  );

  return {
    theme,
    openTradeExtraDetails,
    topSection,
    bottomSection,
    invalidAccountData,
    isTradeClosed,
    annotations,
  };
};

export default useTradeCard;

const VerticalBorder = styled.div`
  border-left: 1px solid ${(p) => p.theme.colors.lightBorderGrey};
  height: 40px;
`;
const InfoWrapper = styled.div`
  display: flex;
  padding-top: 8px;
  width: 100%;
`;
const InfoItem = styled(Flex)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin: auto;
  padding: 0 8px;
`;
const InfoTitle = styled(Title).attrs({
  level: 5,
  type: "secondary",
})``;
const MarginTitle = styled(Title).attrs({
  level: 5,
  type: "secondary",
})``;
const TradeSymbolTitle = styled(Title).attrs({
  level: 4,
})`
  font-weight: ${(p) => p.theme.typography.fontWeights.SEMI_BOLD};
  color: inherit;
`;
const MarginWrapper = styled.div`
  display: flex;
  align-items: center;
`;
const SymbolLeverageWrapper = styled.div`
  margin-left: 12px;
`;
const ClockCircleOutlined = styled(AntClockCircleOutlined)`
  color: ${(p) => p.theme.colors.secondaryGrey};
  margin-right: 4px;
  width: 12px;
  height: 12px;
`;
